'use client';

import { useTranslations } from 'next-intl';

import { alpha } from '@mui/material/styles';
import { Box, Link, Stack, Typography } from '@mui/material';

import { Iconify } from 'src/components/iconify';

export function ContactInfo() {
  const t = useTranslations('Contact.info');

  const ITEMS = [
    {
      icon: 'solar:letter-bold-duotone',
      label: t('emailLabel'),
      value: 'support@testelope.com',
      href: 'mailto:support@testelope.com',
    },
    {
      icon: 'solar:map-point-bold-duotone',
      label: t('addressLabel'),
      value: t('addressValue'),
    },
    {
      icon: 'solar:clock-circle-bold-duotone',
      label: t('responseLabel'),
      value: t('responseValue'),
    },
  ];

  return (
    <Box>
      <Typography
        component="h2"
        sx={{
          fontFamily: 'Barlow, sans-serif',
          fontWeight: 800,
          fontSize: { xs: 22, md: 28 },
          letterSpacing: '-0.5px',
          color: 'text.primary',
          mb: 2,
        }}
      >
        {t('title')}
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: 14, md: 15 },
          lineHeight: 1.7,
          color: 'text.secondary',
          mb: 5,
          textWrap: 'pretty',
        }}
      >
        {t('description')}
      </Typography>

      <Stack spacing={2.5} sx={{ mb: 6 }}>
        {ITEMS.map((item) => (
          <Stack
            key={item.label}
            direction="row"
            alignItems="flex-start"
            spacing={2}
            sx={{
              '&:hover .contact-info-icon': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.25),
              },
            }}
          >
            <Box
              className="contact-info-icon"
              sx={(theme) => ({
                width: 44,
                height: 44,
                borderRadius: 1.5,
                bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'grey.100',
                border: '1px solid',
                borderColor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.06)
                    : 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'background 0.2s, border-color 0.2s',
              })}
            >
              <Iconify icon={item.icon} width={20} sx={{ color: 'primary.main' }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  color: 'text.disabled',
                  mb: 0.5,
                }}
              >
                {item.label}
              </Typography>
              {item.href ? (
                <Link
                  href={item.href}
                  underline="none"
                  sx={{
                    fontSize: 15,
                    fontWeight: 500,
                    color: 'text.primary',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  {item.value}
                </Link>
              ) : (
                <Typography sx={{ fontSize: 15, fontWeight: 500, color: 'text.primary' }}>
                  {item.value}
                </Typography>
              )}
            </Box>
          </Stack>
        ))}
      </Stack>

      {/* Response card */}
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'grey.900',
          borderRadius: 2,
          p: 3.5,
          overflow: 'hidden',
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: -40,
            right: -40,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: (theme) =>
              `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.4)} 0%, transparent 70%)`,
          }}
        />
        <Typography
          sx={{
            fontFamily: 'Barlow, sans-serif',
            fontWeight: 700,
            fontSize: 16,
            color: 'common.white',
            mb: 1,
            position: 'relative',
          }}
        >
          {t('cardTitle')}
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            color: alpha('#ffffff', 0.45),
            lineHeight: 1.6,
            position: 'relative',
          }}
        >
          {t('cardDescription')}
        </Typography>
      </Box>
    </Box>
  );
}
